import React, { createContext, useContext, useCallback } from 'react';
import { useQuery, gql } from '@apollo/client';
import { useDispatch } from 'react-redux';
import { setUserProfile } from '../store/userSlice';
import { GET_PROFILE } from './Queries';

// Profile context for managing profile updates across the app
const ProfileContext = createContext({
  refetchProfile: () => {},
  profileLoading: false,
  profileData: null,
});

export const ProfileProvider = ({ children }) => {
  const dispatch = useDispatch();
  
  const { data: profileData, loading: profileLoading, refetch: refetchProfile } = useQuery(
    GET_PROFILE,
    {
      onCompleted: (data) => {
        if (data?.getProfile?.profile) {
          // Update Redux store with fresh profile data
          dispatch(setUserProfile(data.getProfile.profile));
        }
      },
      onError: (error) => {
        console.error('Profile fetch error:', error);
      },
      // Enable polling for real-time updates (optional)
      pollInterval: 0, // Set to 30000 for 30-second polling if needed
      notifyOnNetworkStatusChange: true,
    }
  );

  const handleRefetchProfile = useCallback(async () => {
    try {
      const result = await refetchProfile();
      if (result.data?.getProfile?.profile) {
        dispatch(setUserProfile(result.data.getProfile.profile));
      }
      return result;
    } catch (error) {
      console.error('Error refetching profile:', error);
      throw error;
    }
  }, [refetchProfile, dispatch]);

  return (
    <ProfileContext.Provider
      value={{
        refetchProfile: handleRefetchProfile,
        profileLoading,
        profileData,
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
};

export const useProfile = () => {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error('useProfile must be used within a ProfileProvider');
  }
  return context;
};

export default ProfileContext;
